import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import type { IconNames } from "@/v2-ui/icon/icon.types"
import Icon from "@/v2-ui/icon"
import DropdownMenu from "@/v2-ui/dropdownMenu"
import Link, {
  type LinkProps,
} from "@/v2-backoffice-client/components/link/Link"
import type { ActionMenuItemVariant } from "@/v2-backoffice-client/components/actionMenu/ActionMenuItem"

export type ActionMenuLinkItemProps = Omit<
  ComponentProps<typeof DropdownMenu.Item>,
  "asChild" | "onSelect"
> & {
  to: LinkProps["to"]
  /** Forced when the href isn't self-evidently absolute; Link infers it otherwise. */
  external?: boolean
  iconSrc?: IconNames
  iconSize?: number
  variant?: ActionMenuItemVariant
}

/**
 * A menu entry that goes somewhere, as opposed to ActionMenu.Item, which does
 * something. It renders an actual anchor inside the menu item, so the
 * destination survives right-click, middle-click and copy-link - an
 * `onSelect` that calls `window.open` throws all of that away, and gets eaten
 * by popup blockers besides.
 */
function ActionMenuLinkItem(props: ActionMenuLinkItemProps) {
  const {
    to,
    external,
    iconSrc,
    iconSize = 14,
    variant,
    disabled,
    className,
    children,
    ...rest
  } = props

  return (
    <DropdownMenu.Item
      asChild
      disabled={disabled}
      className={cn(
        "action-menu-item",
        variant && `action-menu-item-${variant}`,
        // A disabled anchor is still a working link to the browser, so the
        // pointer has to be taken away explicitly.
        disabled && "pointer-events-none opacity-50",
        className,
      )}
      {...rest}
    >
      <Link
        to={to}
        external={external}
        variant="unstyled"
        // Suppress Link's trailing link/external glyph: the leading icon
        // already carries the destination, as on every other menu entry.
        iconSrc={null}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : undefined}
      >
        {iconSrc && <Icon src={iconSrc} size={iconSize} />}
        {children}
      </Link>
    </DropdownMenu.Item>
  )
}

export default ActionMenuLinkItem
